import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import OTPInput from "react-otp-input";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../services/interceptor";

export default function Signup() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { loginUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (loginUser) {
      navigate("/dashboard");
    }
  }, [loginUser, navigate]);

  const notifyError = (message = "Something went wrong") => {
    toast.error(message, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: true,
      theme: "colored",
    });
  };

  const notifySuccess = (message = "Success") => {
    toast.success(message, {
      position: "top-center",
      autoClose: 2500,
      hideProgressBar: true,
      theme: "colored",
    });
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      notifyError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/register`, {
        name,
        mobile,
        email,
        password,
      });

      setOtpSent(true);
      notifySuccess("OTP sent to your email");
    } catch (error) {
      console.error("Signup failed", error);
      const msg =
        error?.response?.data?.message || "Signup failed. Please try again.";
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/verify-otp`, {
        email,
        otp,
      });
      setIsVerified(true);
      notifySuccess("Verification successful — you can now login");
      navigate("/login");
    } catch (error) {
      console.error("OTP verification failed", error);
      const msg =
        error?.response?.data?.message || "Invalid OTP. Please try again.";
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (otpValue) => {
    setOtp(otpValue);
  };

  // subtle animation variant
  const card = {
    hidden: { opacity: 0, y: 12, scale: 0.995 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4 } },
  };

  // OTP UI
  if (otpSent && !isVerified) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.45, ease: "easeInOut" }}
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-indigo-50 p-6"
      >
        <motion.div
          variants={card}
          initial="hidden"
          animate="visible"
          className="w-full max-w-md bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-8"
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Verify OTP</h2>
            <p className="text-sm text-gray-500 mt-1">
              An OTP has been sent to <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleOtpSubmit} className="space-y-5">
            <div>
              <OTPInput
                value={otp}
                onChange={handleOTPChange}
                numInputs={6}
                isInputNum
                shouldAutoFocus
                renderInput={(props) => (
                  <input
                    {...props}
                    placeholder="-"
                    className="w-12 h-12 border border-gray-200 bg-gray-50 rounded-md text-center text-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    style={{ boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.08)" }}
                  />
                )}
                containerStyle={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || otp.length < 6}
              className="w-full inline-flex items-center justify-center gap-3 rounded-xl px-4 py-3 bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? (
                <svg
                  className="animate-spin h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" className="opacity-75" />
                </svg>
              ) : null}
              <span>{loading ? "Verifying..." : "Verify"}</span>
            </button>

            <div className="text-center text-sm text-gray-500">
              <button
                type="button"
                onClick={() => {
                  // Allow user to resend/return to signup — simple UX: reset otpSent so signup form shows again
                  setOtpSent(false);
                  setOtp("");
                }}
                className="text-indigo-600 font-medium hover:underline"
              >
                Back to signup
              </button>
            </div>
          </form>

          <ToastContainer />
        </motion.div>
      </motion.div>
    );
  }

  // Signup UI
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, ease: "easeInOut" }}
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-indigo-50 p-6"
    >
      <motion.div
        variants={card}
        initial="hidden"
        animate="visible"
        className="w-full max-w-md bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-8"
      >
        <div className="mb-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800">Create account</h2>
          <p className="text-sm text-gray-500 mt-1">Register to get started</p>
        </div>

        <form onSubmit={handleSignupSubmit} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Full name</label>
            <input
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Mobile number</label>
            <input
              type="text"
              placeholder="+91 55555 55555"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              required
              className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Email</label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Confirm password</label>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 rounded-xl px-4 py-3 bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 disabled:opacity-60"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" className="opacity-75" />
              </svg>
            ) : null}
            <span>{loading ? "Signing up..." : "Sign up"}</span>
          </button>

          <div className="text-center mt-2 text-sm text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="text-indigo-600 font-medium hover:underline">
              Log in
            </Link>
          </div>
        </form>

        <ToastContainer />
      </motion.div>
    </motion.div>
  );
}
